import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronRight, RefreshCw, Rocket, ArrowRight, Copy } from "lucide-react";
import { toast } from "sonner";

const STYLE = {
  ok: { Icon: CheckCircle2, cls: "text-cc-green", ring: "border-cc-green/30 bg-cc-green/[0.04]", label: "Ready" },
  warn: { Icon: AlertTriangle, cls: "text-amber-600", ring: "border-amber-300 bg-amber-50/50", label: "Review" },
  fail: { Icon: XCircle, cls: "text-red-600", ring: "border-red-300 bg-red-50/50", label: "Needs fixing" },
};

function CheckRow({ c }) {
  const [open, setOpen] = useState(c.status === "fail");
  const navigate = useNavigate();
  const s = STYLE[c.status] || STYLE.warn;
  return (
    <div className={`rounded-xl border ${s.ring} transition-colors`} data-testid={`check-${c.key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-start gap-3 px-5 py-4 text-left">
        <s.Icon className={`w-5 h-5 mt-0.5 shrink-0 ${s.cls}`} />
        <span className="flex-1 min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-zinc-900">{c.label}</span>
            {c.status === "ok"
              ? <span className="text-xs font-medium text-cc-green">{c.ok_text}</span>
              : <span className={`text-xs font-bold ${s.cls}`} data-testid={`check-count-${c.key}`}>{c.count} to fix</span>}
          </span>
          <span className="block text-sm text-zinc-500 mt-1">{c.why}</span>
        </span>
        {c.status !== "ok" && (open ? <ChevronDown className="w-4 h-4 text-zinc-400 mt-1" /> : <ChevronRight className="w-4 h-4 text-zinc-400 mt-1" />)}
      </button>
      {open && c.status !== "ok" && (
        <div className="px-5 pb-4 pl-13">
          <p className="text-xs text-zinc-600 bg-white border rounded-md px-3 py-2 mb-3"><b>How to fix:</b> {c.fix_hint}</p>
          <ul className="space-y-1.5" data-testid={`check-items-${c.key}`}>
            {c.items.map((it) => (
              <li key={it.id} className="flex items-center justify-between gap-3 text-sm bg-white border rounded-md px-3 py-2">
                <span className="min-w-0">
                  <span className="font-medium text-zinc-800 block truncate">{it.name}</span>
                  {it.sub && <span className="text-xs text-zinc-400 block truncate">{it.sub}</span>}
                </span>
                <button onClick={() => navigate(it.path)} className="text-xs font-semibold text-cc-blue hover:underline shrink-0">Fix →</button>
              </li>
            ))}
          </ul>
          {c.truncated > 0 && <p className="text-xs text-zinc-400 mt-2">+ {c.truncated} more</p>}
          <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate(c.fix_path)} data-testid={`check-open-${c.key}`}>
            Open {c.fix_path.replace("/", "")} <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

function SsoPanel() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/auth/microsoft/config").then(({ data }) => setCfg(data)).catch(() => setCfg(null)); }, []);
  if (!cfg) return null;
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  const rows = [["Redirect URI to register", cfg.redirect_uri_in_use], ["Application (client) ID", cfg.client_id], ["Directory (tenant) ID", cfg.tenant_id]];
  return (
    <Card className="mt-6" title="Microsoft sign-in (SSO) settings" data-testid="sso-panel">
      <p className="text-sm text-zinc-500 mb-3">
        If staff see <b>“the redirect URI is invalid”</b> when signing in with Microsoft, an Entra admin must add
        the address below <i>exactly</i> to the app registration: <b>Entra admin center → App registrations →
        your app → Authentication → Add a platform → Web → Redirect URIs</b>. Every environment needs its own
        entry, so the preview link and your live domain are two separate entries.
      </p>
      <div className="space-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-wrap items-center justify-between gap-3 border rounded-md px-3 py-2">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold">{label}</p>
              <p className="font-mono text-xs text-zinc-800 break-all" data-testid={`sso-${label.split(" ")[0].toLowerCase()}`}>{value || "not set"}</p>
            </div>
            {value && <Button size="sm" variant="outline" className="h-8" onClick={() => copy(value)} data-testid={`sso-copy-${label.split(" ")[0].toLowerCase()}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Readiness() {  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/readiness"); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const { summary, checks, context } = data;

  return (
    <div>
      <PageHeader title="Go-Live Checklist" subtitle="Everything that would quietly break the workflow if it's left unset" />

      <div className={`rounded-xl border p-6 mb-6 ${summary.ready ? "border-cc-green/30 bg-cc-green/[0.05]" : "border-red-300 bg-red-50/60"}`} data-testid="readiness-summary">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Rocket className={`w-6 h-6 mt-0.5 ${summary.ready ? "text-cc-green" : "text-red-600"}`} />
            <div>
              <h2 className="text-lg font-semibold text-zinc-900" data-testid="readiness-headline">
                {summary.ready ? "You're clear to go live" : `${summary.blocking} thing${summary.blocking === 1 ? "" : "s"} still blocking go-live`}
              </h2>
              <p className="text-sm text-zinc-600 mt-1">
                {summary.passing} of {summary.total} checks passing · {summary.warnings} to review ·
                {" "}{context.facilities} active facilit{context.facilities === 1 ? "y" : "ies"},
                {" "}{context.schools} school{context.schools === 1 ? "" : "s"} across {context.states.length} state{context.states.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={load} disabled={busy} data-testid="readiness-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Re-check
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {["fail", "warn", "ok"].flatMap((st) => checks.filter((c) => c.status === st)).map((c) => <CheckRow key={c.key} c={c} />)}
      </div>

      <SsoPanel />

      <Card className="mt-6" title="Still carrying practice data?">
        <p className="text-sm text-zinc-500">
          Super Admins can wipe every demo/test school, student and placement from <b>Settings → Data management</b>.
          Facilities, settings, templates, Lists &amp; Options and Microsoft sign-ins are always kept.
        </p>
      </Card>
    </div>
  );
}
